import { useState, useRef, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthProvider';
import { Plus, Trash2, ShoppingBag, UploadCloud, Target, DollarSign, PenTool, Link as LinkIcon, Image as ImageIcon, Zap } from 'lucide-react';
import toast from 'react-hot-toast';

interface CustomProduct {
  id: string;
  name: string;
  price: string;
  permalink: string;
  image_url: string;
  description: string;
}

interface CustomProductCreatorProps {
  onCreated: () => void;
}

export default function CustomProductCreator({ onCreated }: CustomProductCreatorProps) {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [campaign, setCampaign] = useState({
    name: '',
    daily_budget: 20,
    duration_days: 7,
    meta_page_id: '',
  });

  const [socialAccounts, setSocialAccounts] = useState<any[]>([]);

  const [products, setProducts] = useState<CustomProduct[]>([
    { id: Math.random().toString(36).substring(2), name: '', price: '', permalink: '', image_url: '', description: '' }
  ]);

  const [isUploading, setIsUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    if (user) {
      supabase.from('social_accounts')
        .select('*')
        .eq('owner_user_id', user.id)
        .eq('platform', 'facebook')
        .then(({ data }) => {
          if (data) {
            setSocialAccounts(data);
            if (data.length > 0) {
              setCampaign(prev => ({ ...prev, meta_page_id: prev.meta_page_id || data[0].page_id }));
            }
          }
        });
    }
  }, [user]);

  const updateProduct = (id: string, patch: Partial<CustomProduct>) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
  };

  const addProduct = () => {
    if (products.length >= 10) return toast.error('Maksymalnie 10 produktów w karuzeli');
    setProducts(prev => [...prev, { id: Math.random().toString(36).substring(2), name: '', price: '', permalink: '', image_url: '', description: '' }]);
  };

  const removeProduct = (id: string) => {
    if (products.length <= 1) return toast.error('Musisz dodać co najmniej jeden produkt');
    setProducts(prev => prev.filter(p => p.id !== id));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, productId: string) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;
      const { error: uploadError } = await supabase.storage.from('media').upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('media').getPublicUrl(filePath);
      updateProduct(productId, { image_url: publicUrl });
      toast.success('Zdjęcie dodane');
    } catch (err: any) {
      toast.error(err.message || 'Błąd uploadu');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const validate = () => {
    if (!campaign.meta_page_id) return 'Wybierz Stronę (Fanpage)';
    if (!campaign.name) return 'Podaj nazwę kampanii';
    if (products.length === 0) return 'Dodaj co najmniej jeden produkt';
    for (let i = 0; i < products.length; i++) {
      const p = products[i];
      if (!p.name) return `Produkt ${i + 1}: Brak nazwy`;
      if (!p.permalink) return `Produkt ${i + 1}: Brak linku`;
      if (!p.image_url) return `Produkt ${i + 1}: Brak zdjęcia`;
    }
    return null;
  };

  const publishCampaign = async () => {
    const err = validate();
    if (err) return toast.error(err);
    if (!user) return;

    try {
      setPublishing(true);
      const isCarousel = products.length > 1;
      toast.loading(isCarousel ? 'Tworzenie kampanii karuzelowej...' : 'Tworzenie kampanii produktowej...', { id: 'pub' });

      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + campaign.duration_days);

      // Intelligent Targeting Logic (Same as Woo Products)
      const getIntelligentTargeting = (productName: string) => {
        const name = productName.toLowerCase();
        let age_min = 25; let age_max = 65; let genders = [0];
        if (name.includes('zabieg') || name.includes('spa') || name.includes('twarz') || name.includes('krem') || name.includes('włosy') || name.includes('paznokcie')) {
          age_min = 20; age_max = 55; genders = [2];
        } else if (name.includes('lampa') || name.includes('żarówka') || name.includes('meble') || name.includes('dom')) {
          age_min = 28; age_max = 65;
        } else if (name.includes('kabel') || name.includes('usb') || name.includes('elektronika')) {
          age_min = 18; age_max = 50;
        }
        return { age_min, age_max, genders, geo_locations: { countries: ['PL'] } };
      };

      const targeting = getIntelligentTargeting(products[0].name);

      // Main Campaign URL
      const destUrl = isCarousel ? new URL(products[0].permalink).origin : products[0].permalink;

      // Create Campaign
      const { data: camp, error: campErr } = await supabase.from('ad_campaigns').insert({
        user_id: user.id,
        name: campaign.name,
        objective: 'OUTCOME_SALES',
        daily_budget: campaign.daily_budget,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        destination_url: destUrl,
        targeting: targeting,
        status: 'draft',
        source_type: 'custom_product', // NEW source type
        product_data: isCarousel ? products : products[0],
        meta_page_id: campaign.meta_page_id,
      }).select('id').single();

      if (campErr || !camp) throw new Error(campErr?.message || 'Nie udało się zapisać kampanii');

      // Check if any product has a price set
      const hasAnyPrice = products.some(p => p.price && parseFloat(p.price) > 0);

      if (isCarousel) {
        // CAROUSEL
        const carouselText = hasAnyPrice
          ? `🛒 Sprawdź naszą ofertę! Zobacz nasze polecane produkty w świetnych cenach. Kliknij i zamów!`
          : `🚀 Sprawdź naszą ofertę! Odkryj nasze polecane produkty. Kliknij i dowiedz się więcej!`;

        await supabase.from('ad_campaign_creatives').insert({
          campaign_id: camp.id,
          media_url: 'carousel',
          media_type: 'carousel',
          primary_text: carouselText,
          headline: 'Katalog Produktów',
          sort_order: 0,
          carousel_data: products, // matches format needed by publisher
        });
      } else {
        // SINGLE PRODUCT - Create multiple variants for AI optimization
        const p = products[0];
        const hasPrice = p.price && parseFloat(p.price) > 0;
        const priceFormatted = hasPrice ? parseFloat(p.price).toFixed(2) : null;
        
        // Generate different ad copy variants depending on whether price is set
        const variants = hasPrice
          ? [
              `${p.name} — teraz za ${priceFormatted} PLN! ${p.description ? p.description + ' ' : ''}Kliknij i sprawdź ofertę.`,
              `🔥 Bestseller: ${p.name}! Zamów już dziś w rewelacyjnej cenie ${priceFormatted} PLN.`,
              `Odkryj ${p.name}! Tylko ${priceFormatted} PLN. Nie przegap tej okazji, kliknij i kup teraz!`
            ]
          : [
              `${p.name} — ${p.description ? p.description + ' ' : ''}Kliknij i dowiedz się więcej!`,
              `🔥 Polecamy: ${p.name}! Sprawdź szczegóły i skorzystaj z oferty już dziś.`,
              `Odkryj ${p.name}! Nie przegap — kliknij i zobacz, co dla Ciebie przygotowaliśmy!`
            ];

        const creativesToInsert = variants.map((text, idx) => ({
          campaign_id: camp.id, 
          media_url: p.image_url, 
          media_type: 'image',
          primary_text: text,
          headline: p.name, 
          sort_order: idx,
        }));

        await supabase.from('ad_campaign_creatives').insert(creativesToInsert);
      }

      toast.loading('Wysyłanie do Meta API...', { id: 'pub' });

      // Trigger Publisher
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ads-campaign-publisher`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ campaign_id: camp.id }),
      });
      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.error || 'Błąd serwera Meta API');
      }

      if (result.success) {
        toast.success(isCarousel ? '🎠 Karuzela AKTYWNA!' : '✅ Kampania produktowa AKTYWNA!', { id: 'pub', duration: 5000 });
        onCreated();
      } else {
        toast.error('Niektóre kreacje nie zostały opublikowane poprawnie', { id: 'pub' });
        onCreated();
      }

    } catch (e: any) {
      console.error(e);
      toast.error(e.message || 'Błąd', { id: 'pub' });
    } finally {
      setPublishing(false);
    }
  };

  const inputCls = "w-full px-4 py-3 bg-[#1e293b]/50 border border-slate-700 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all";
  const labelCls = "flex items-center gap-2 text-sm font-bold text-slate-300 mb-2";

  return (
    <div className="bg-[#0f172a] border border-slate-800 rounded-3xl overflow-hidden shadow-xl animate-in fade-in">
      <div className="p-8 border-b border-slate-800/80">
        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
          <div className="p-2.5 bg-indigo-500/10 rounded-xl"><ShoppingBag className="w-6 h-6 text-indigo-400" /></div>
          Twój Produkt (Ręczny Katalog)
        </h2>
        <p className="text-slate-400 mt-1">Stwórz inteligentną kampanię produktową lub karuzelę ze swoimi własnymi produktami, bez podłączania WooCommerce.</p>
      </div>

      <div className="p-8 space-y-8">
        {/* Campaign Settings */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-[#1e293b]/30 p-6 rounded-2xl border border-slate-800">
          <div className="md:col-span-3">
            <label className={labelCls}>Strona Facebook (Fanpage)</label>
            <select 
              value={campaign.meta_page_id} 
              onChange={e => setCampaign({ ...campaign, meta_page_id: e.target.value })} 
              className={inputCls}
            >
              <option value="" disabled>Wybierz stronę...</option>
              {socialAccounts.map(acc => (
                <option key={acc.id} value={acc.page_id}>{acc.page_name}</option>
              ))}
            </select>
            {socialAccounts.length === 0 && (
              <p className="text-xs text-rose-400 mt-2 font-bold">Brak podłączonych stron. Przejdź do Ustawień Ads.</p>
            )}
          </div>
          <div>
            <label className={labelCls}><PenTool className="w-4 h-4 text-indigo-400" /> Nazwa kampanii</label>
            <input type="text" placeholder="np. Promocja Własnych Produktów" value={campaign.name} onChange={e => setCampaign({ ...campaign, name: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}><DollarSign className="w-4 h-4 text-emerald-400" /> Budżet dzienny (PLN)</label>
            <input type="number" min={5} value={campaign.daily_budget} onChange={e => setCampaign({ ...campaign, daily_budget: Number(e.target.value) })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}><Target className="w-4 h-4 text-blue-400" /> Czas trwania (Dni)</label>
            <input type="number" min={1} max={365} value={campaign.duration_days} onChange={e => setCampaign({ ...campaign, duration_days: Number(e.target.value) })} className={inputCls} />
          </div>
        </div>

        {/* Products List */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">
              Twoje Produkty ({products.length})
              {products.length > 1 && <span className="ml-3 px-2.5 py-1 bg-indigo-500/20 text-indigo-400 rounded-full text-xs border border-indigo-500/30">Zostanie utworzona Karuzela</span>}
              {products.length === 1 && <span className="ml-3 px-2.5 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs border border-blue-500/30">Zostanie utworzona Pojedyncza Reklama (A/B Test)</span>}
            </h3>
            <button onClick={addProduct} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors border border-slate-700">
              <Plus className="w-4 h-4" /> Dodaj Produkt
            </button>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {products.map((p, idx) => (
              <div key={p.id} className="bg-[#1e293b]/50 p-6 rounded-2xl border border-slate-700/60 relative group">
                <div className="absolute top-4 right-4 flex gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-800 text-slate-400 text-xs font-bold">{idx + 1}</span>
                  {products.length > 1 && (
                    <button onClick={() => removeProduct(p.id)} className="w-6 h-6 rounded-full bg-red-500/10 text-red-400 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-1">
                    <label className={labelCls}>Zdjęcie Produktu</label>
                    <div className="relative group/img">
                      {p.image_url ? (
                        <div className="relative aspect-square rounded-xl overflow-hidden border border-slate-700 bg-[#0f172a]">
                          <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                            <label className="cursor-pointer text-white flex flex-col items-center">
                              <UploadCloud className="w-6 h-6 mb-1" />
                              <span className="text-xs font-bold">Zmień</span>
                              <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, p.id)} disabled={isUploading} />
                            </label>
                          </div>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center aspect-square border-2 border-dashed border-slate-600 rounded-xl bg-slate-800/50 hover:bg-slate-700/50 hover:border-indigo-500/50 transition-colors cursor-pointer text-slate-400 hover:text-indigo-400">
                          <ImageIcon className="w-8 h-8 mb-2" />
                          <span className="text-sm font-semibold text-center px-4">Kliknij aby wgrać</span>
                          <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, p.id)} disabled={isUploading} />
                        </label>
                      )}
                    </div>
                  </div>

                  <div className="md:col-span-2 space-y-4">
                    <div>
                      <label className={labelCls}>Nazwa Produktu</label>
                      <input type="text" value={p.name} onChange={e => updateProduct(p.id, { name: e.target.value })} className={inputCls} placeholder="np. Modny Sweter" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>
                          <DollarSign className="w-4 h-4 text-emerald-400" />
                          Cena (PLN)
                          <span className="text-xs font-normal text-slate-500 ml-1">(opcjonalnie)</span>
                        </label>
                        <input type="number" step="0.01" min="0" value={p.price} onChange={e => updateProduct(p.id, { price: e.target.value })} className={inputCls} placeholder="np. 99.99 lub zostaw puste" />
                        <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                          Zostaw puste dla lejków marketingowych, darmowych ofert lub produktów bez ustalonej ceny.
                        </p>
                      </div>
                      <div>
                        <label className={labelCls}><LinkIcon className="w-4 h-4 text-slate-400" /> Link URL</label>
                        <input type="url" value={p.permalink} onChange={e => updateProduct(p.id, { permalink: e.target.value })} className={inputCls} placeholder="https://..." />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Krótki Opis (opcjonalnie)</label>
                      <textarea value={p.description} onChange={e => updateProduct(p.id, { description: e.target.value })} className={inputCls + " h-20 resize-none"} placeholder="Wpisz zalety produktu..." />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end pt-6 border-t border-slate-800">
          <button
            onClick={publishCampaign}
            disabled={publishing || isUploading}
            className={`px-8 py-4 rounded-xl font-bold flex items-center gap-2 text-lg shadow-xl shadow-indigo-500/20 transition-all ${
              publishing ? 'bg-indigo-600/50 text-indigo-200 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-indigo-500/40 hover:scale-105'
            }`}
          >
            {publishing ? (
              <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Publikowanie...</>
            ) : (
              <><Zap className="w-5 h-5" /> Rozpocznij Publikację ({products.length > 1 ? 'Karuzela' : 'Jedno zdjęcie'})</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
